import type { AcademyOperatingProfileInput, User } from '@court-sync/shared';
import { coachService } from '@/services/coach';

export function isSoloCoachOperatingProfile(
  profile: AcademyOperatingProfileInput | null | undefined
): boolean {
  return profile?.operatingRole === 'solo_coach';
}

/** Owner selected solo coach or owner-coach — they teach and should be on the coach roster. */
export function isOwnerWhoTeachesProfile(
  profile: AcademyOperatingProfileInput | null | undefined
): boolean {
  const role = profile?.operatingRole;
  return role === 'solo_coach' || role === 'owner_coach';
}

export function shouldShowSelfCoachPrompt(params: {
  coachesCount: number;
  dismissed: boolean;
  operatingProfile: AcademyOperatingProfileInput | null | undefined;
}): boolean {
  if (params.coachesCount > 0) return false;
  if (params.dismissed) return false;
  if (isOwnerWhoTeachesProfile(params.operatingProfile)) return false;
  return true;
}

export function shouldAutoAddOwnerAsCoach(params: {
  coachesCount: number;
  dismissed: boolean;
  operatingProfile: AcademyOperatingProfileInput | null | undefined;
}): boolean {
  if (params.coachesCount > 0) return false;
  if (params.dismissed) return false;
  return isOwnerWhoTeachesProfile(params.operatingProfile);
}

/** Self-add API requires non-empty first and last name plus email (DB NOT NULL). */
export function canAutoAddCoachWithProfile(
  user: Pick<User, 'email' | 'firstName' | 'lastName'> | null | undefined
): boolean {
  if (!user?.email?.trim()) return false;
  if (!user.firstName?.trim()) return false;
  if (!user.lastName?.trim()) return false;
  return true;
}

/** @deprecated Use shouldAutoAddOwnerAsCoach */
export const shouldAutoAddSoloCoach = shouldAutoAddOwnerAsCoach;

export async function tryAutoAddOwnerAsCoach(params: {
  academyId: string;
  user: Pick<User, 'email' | 'firstName' | 'lastName' | 'phone'>;
  operatingProfile: AcademyOperatingProfileInput | null | undefined;
  coachesCount?: number;
}): Promise<boolean> {
  if (!canAutoAddCoachWithProfile(params.user)) return false;

  if (
    !shouldAutoAddOwnerAsCoach({
      coachesCount: params.coachesCount ?? 0,
      dismissed: false,
      operatingProfile: params.operatingProfile,
    })
  ) {
    return false;
  }

  try {
    const response = await coachService.selfAdd(params.academyId, {
      firstName: params.user.firstName || '',
      lastName: params.user.lastName || '',
      email: params.user.email,
      phone: params.user.phone || undefined,
    });
    return response.success === true;
  } catch (error) {
    console.error('Failed to auto-add owner as coach during onboarding:', error);
    return false;
  }
}
